"use client";

import { useTranslations } from "next-intl";
import React, {
    type FocusEvent,
    useCallback,
    useEffect,
    useState,
} from "react";
import { cn } from "../../helpers/classNames";
import { getWindow } from "../../helpers/getWindow";
import { isDachProduct } from "../../helpers/isDachProduct";
import { getRoute } from "../../helpers/urls/getRoute";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { useIsPageChange } from "../../hooks/useIsPageChange";
import { useLocation } from "../../network/matlocq/queries/Location";
import { useAutosuggestSearch } from "../../network/matlocq/queries/Search";
import {
    useCountry,
    usePageParams,
    usePageType,
} from "../ApplicationState/hooks";
import { Button } from "../Button";
import styles from "./index.module.scss";
import { SuggestList } from "./suggestList";

export const AutoSuggest: React.FC = () => {
    const country = useCountry();
    const pageType = usePageType();
    const window = getWindow();
    const t = useTranslations("component.autoSuggest");
    const [value, setValue] = useState<string>("");
    const debouncedValue = useDebouncedValue<string>(value, 100);
    const { data: suggestions } = useAutosuggestSearch(debouncedValue);
    let placeholder = t("defaultPlaceholder");
    const [overlayShown, setOverlayShown] = useState(false);
    const pageParams = usePageParams();
    const { data: location } = useLocation(pageParams.code ?? null);
    const isPageChange = useIsPageChange();
    const showOverlay = useCallback(
        (show = true): void => {
            requestAnimationFrame(() => {
                setOverlayShown(show);
                if (!show) {
                    // when the menu suggestions are scrolled, reset and scroll to top
                    window?.scrollTo(0, 0);
                }
            });
        },
        [window]
    );

    if (location && pageType?.isAnyForecastPage) {
        const { name, zipcodes, country, admin1 } = location;
        const zip = zipcodes[0];
        if (zip && name && country.code) {
            placeholder = `${zip} - ${name} - ${country.code}`;
        } else if (name && admin1.location.name && country.code) {
            placeholder = `${name} - ${admin1.location.name} - ${country.code}`;
        }
    }

    useEffect(() => {
        if (isPageChange) {
            requestAnimationFrame(() => {
                setOverlayShown(false);
                setValue("");
            });
        }
    }, [isPageChange]);

    if (pageType?.isPlainPage) {
        return null;
    }

    return (
        <div
            className={cn(
                styles.container,
                !isDachProduct(country) && styles.containerProduct
            )}
            id="autosuggest-container"
        >
            <form
                className={cn(
                    styles.form,
                    overlayShown && styles.formOpen,
                    !isDachProduct(country) && styles.formProduct
                )}
                role="search"
                action={getRoute({
                    parameters: {
                        pageName: "searchResults",
                        searchTerm: value,
                    },
                    country,
                })}
            >
                <input
                    className={styles.input}
                    role="searchbox"
                    name="q"
                    required
                    value={value}
                    placeholder={placeholder}
                    autoComplete="off"
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void =>
                        setValue(e.target.value)
                    }
                    onFocus={(): void => {
                        showOverlay(true);
                    }}
                    onBlur={(e: FocusEvent): void => {
                        const tagName =
                            e.relatedTarget?.tagName.toLowerCase() ?? "";

                        if (!["button", "a"].includes(tagName)) {
                            e.preventDefault();
                            showOverlay(false);
                        }
                    }}
                />
                <Button
                    variant="unstyled"
                    className={styles.magnifier}
                    type="submit"
                    aria-label={t("magnifier.ariaLabel")}
                >
                    <span className="icon-magnifier" />
                </Button>
            </form>
            {overlayShown && (
                <SuggestList
                    searchResult={suggestions ?? undefined}
                    searchValue={value}
                    onItemClick={(): void => {
                        showOverlay(false);
                        setValue("");
                    }}
                />
            )}
        </div>
    );
};
